import React, { useMemo } from "react";

import { ButtonStandard, Text } from "@performyard/py-components";

import { type BulkActionKey, formCountLabel, getAvailableBulkActions } from "../bulk-actions";

type SelectionBannerProps = {
	selectedCount: number;
	totalCount: number;
	/** True when every form in the cycle is selected, which hides the "Select All" link */
	allRowsSelected: boolean;
	/** The acting user's permissions on the cycle; each action shows only if its own is present */
	permissions: string[];
	disabled?: boolean;
	/**
	 * True while the forms are still streaming in. Select All only reaches the rows already in the
	 * cache, so it stays hidden until the whole cycle has arrived and the count is the real total.
	 */
	isLoadingRows?: boolean;
	onSelectAll: () => void;
	onClearSelection: () => void;
	onActionSelected: (_actionKey: BulkActionKey) => void;
};

/**
 * Shown above the table whenever at least one form is selected. Reports the selected count and
 * offers the two selection shortcuts plus one button per bulk action.
 */
export const SelectionBanner = ({
	selectedCount,
	totalCount,
	allRowsSelected,
	permissions,
	disabled = false,
	isLoadingRows = false,
	onSelectAll,
	onClearSelection,
	onActionSelected,
}: SelectionBannerProps) => {
	const actions = useMemo(() => getAvailableBulkActions(permissions), [permissions]);

	if (actions.length === 0) return null;

	return (
		<div
			className="flex min-h-12 flex-wrap items-center justify-between gap-3 rounded border border-solid border-neutral-2 bg-neutral-1 px-4 py-2"
			data-testid="review-forms-selection-banner"
		>
			<div className="flex items-center gap-2">
				<Text size="paragraph-3" weight="semi-bold" data-testid="review-forms-selected-count">
					{formCountLabel(selectedCount, { titleCase: true })} Selected
				</Text>

				{!allRowsSelected && !isLoadingRows && (
					<>
						<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
							•
						</Text>
						<button
							type="button"
							onClick={onSelectAll}
							className="cursor-pointer border-none bg-transparent p-0 hover:underline"
							data-testid="review-forms-select-all"
						>
							<Text size="paragraph-3" weight="semi-bold" color="primary-5">
								Select All {formCountLabel(totalCount, { titleCase: true })}
							</Text>
						</button>
					</>
				)}

				<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
					•
				</Text>
				<button
					type="button"
					onClick={onClearSelection}
					className="cursor-pointer border-none bg-transparent p-0 hover:underline"
					data-testid="review-forms-clear-selection"
				>
					<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
						Clear Selection
					</Text>
				</button>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				{actions.map((action) => (
					<div key={action.key} className="w-fit">
						<ButtonStandard
							variant="outline"
							color="neutral"
							size="medium"
							iconLeft={action.icon}
							disabled={disabled}
							onClick={() => onActionSelected(action.key)}
							data-testid={`review-forms-action-${action.key}`}
						>
							{action.buttonLabel}
						</ButtonStandard>
					</div>
				))}
			</div>
		</div>
	);
};

export default SelectionBanner;
